import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  DatePicker,
  renderTimeViewClock,
  TimePicker,
} from "@mui/x-date-pickers";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetApi } from "../hooks/useGetApi";
import {
  addAppointmentApi,
  getAppointmentByIdApi,
} from "../services/appointment.service";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { getUserApi } from "../services/user.service";
import { getUnitApi } from "../services/unit.service";
import { getVisitTypeApi } from "../services/visitType.service";
import { getTransactionTypeApi } from "../services/transaction-type.service";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import DiscountModal from "../components/consultation/modal/discount-modal";
import { createConsultationApi } from "../services/consultation.service";
import ConvertConsultationSchema from "../joi/consultation/convert-consultation.schema";

export default function PatientConvertConsultationOPD() {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Use direct API call instead of useGetApi for single object response
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowLoading, setSelectedRowLoading] = useState(true);
  const [selectedRowError, setSelectedRowError] = useState(false);

  useEffect(() => {
    const fetchAppointmentData = async () => {
      if (!id) {
        setSelectedRowLoading(false);
        return;
      }
      
      setSelectedRowLoading(true);
      setSelectedRowError(false);
      
      try {
        const response = await getAppointmentByIdApi({ id });
        
        if (response?.code === 200 || response?.success) {
          const appointmentData = response.data || response;
          setSelectedRow(appointmentData);
          setSelectedRowError(false);
        } else {
          setSelectedRowError(true);
          setSelectedRow(null);
        }
      } catch (error) {
        setSelectedRowError(true);
        setSelectedRow(null);
      } finally {
        setSelectedRowLoading(false);
      }
    };

    fetchAppointmentData();
  }, [id]);

  const getValidISODateTime = (date, time) => {
    if (!date || !time) return null;
    const combined = `${date}T${time}:00`;
    const parsed = new Date(combined);
    return isNaN(parsed.getTime()) ? null : parsed.toISOString();
  };
  
  const initialState = {
    UHID: "",
    patientId: "",
    unit: null,
    doctorName: null,
    visitType: null,
    consultationType: "OPD",
    consultationTime: null,
    consultationDate: null,
    amount: "",
    doctorNotes: "",
    discount: {
      discountReason: "",
      approvedBy: null,
      discountType: null,
      discountAmount: 0,
      discountPercentage: 0,
    },
    payment: {
      transactionType: null,
      transactionId: null,
      amountPaid: "",
      remarks: "To Reception",
      paymentType: "",
    },
    appointmentId: id,
  };

  const [formData, setFormData] = useState(initialState);
  const [confirmAmountPaid, setConfirmAmountPaid] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isTransactionTypeSet, setIsTransactionTypeSet] = useState(false);
  const [errors, setErrors] = useState([]);
  
  // Updated validation logic to allow partial payments for "Pending Payment" transaction type
  const isPendingPayment = formData?.payment?.transactionType?.transactionMode === "Pending Payment";
  
  // More flexible OPD detection - check for various possible OPD names
  const isOPDVisitType = formData?.visitType?.name === "OPD" || 
                         formData?.visitType?.name === "OPD Visit" ||
                         formData?.visitType?.name === "Old OPD" ||
                         formData?.visitType?.name?.toLowerCase().includes("opd") ||
                         formData?.visitType?.visitType === "OPD";
  
  // Simple condition: if OPD visit type is selected, allow 0; otherwise require positive amounts
  const amountsMatch = isPendingPayment 
    ? (Number(confirmAmountPaid) >= 0 && Number(confirmAmountPaid) <= Number(formData?.payment?.amountPaid || 0))
    : Number(formData?.payment?.amountPaid || 0) === Number(confirmAmountPaid);

  const { dataList: doctorsList } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "users",
  });

  const { dataList: unitList } = useGetApi({
    apiFunction: getUnitApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "units",
  });

  const { dataList: visitTypeList } = useGetApi({
    apiFunction: getVisitTypeApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "visitTypes",
  });

  const { dataList: transactionList } = useGetApi({
    apiFunction: getTransactionTypeApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
  });

  const transactionTypeList = [
    ...(transactionList?.find((option) => option?.transactionMode === "Payment")?.transactionTypes || []),
    ...(transactionList?.find((option) => option?.transactionMode === "Pending Payment")?.transactionTypes || [])
  ];

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    const parsedValue =
      type === "checkbox"
        ? checked
        : type === "number"
        ? value === "" ? "" : Number(value)
        : name === "consultationTime"
        ? dayjs(value).format("HH:mm")
        : name === "consultationDate"
        ? dayjs(value).format("YYYY-MM-DD")
        : value;

    if (
      name === "transactionType" ||
      name === "transactionId" ||
      name === "amountPaid" ||
      name === "remarks" ||
      name === "paymentType"
    ) {
      setFormData((preValue) => ({
        ...preValue,
        payment: {
          ...preValue?.payment,
          [name]: parsedValue,
        },
      }));
      return;
    }
    if (
      name === "discountReason" ||
      name === "approvedBy" ||
      name === "discountType" ||
      name === "discountAmount" ||
      name === "discountPercentage"
    ) {
      setFormData((preValue) => ({
        ...preValue,
        discount: {
          ...preValue?.discount,
          [name]: parsedValue,
        },
      }));
      return;
    }
    
    setFormData((preValue) => ({
      ...preValue,
      [name]: parsedValue,
    }));
  };

  const handleDoctorUnitChange = (e) => {
    const { name, value } = e.target;

    if (name === "doctorName") {
      setFormData((preValue) => ({
        ...preValue,
        [name]: value,
        unit: value?.unit,
      }));
    }

    if (name === "unit") {
      setFormData((preValue) => ({
        ...preValue,
        [name]: value,
        doctor: null,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      formData?.payment?.transactionType &&
      formData?.amount !== formData?.payment?.amountPaid &&
      !isOPDVisitType
    ) {
      toast.error("Amount and Amount to be paid doesn't match.");
      return;
    }

    if (!amountsMatch && !isOPDVisitType) {
      toast.error("Amount and Confirm Amount doesn't match.");
      return;
    }

    const formDataWithId = {
      ...formData,
      visitType: formData?.visitType?._id,
      doctor: formData?.doctorName?._id,
      unit: formData?.unit?._id,
      consultationCharge: formData?.amount,
      consultationDateTime: getValidISODateTime(
        formData?.consultationDate,
        formData?.consultationTime
      ),
      payment: {
        ...formData?.payment,
        transactionType:
          typeof formData?.payment?.transactionType === "object"
            ? formData?.payment?.transactionType?._id
            : formData?.payment?.transactionType,
      },
    };

    if (
      selectedRow?.paymentStatus === "paid" 
      // !formData?.payment?.transactionType
    ) {
      delete formDataWithId.payment;
      delete formDataWithId.discount;
      delete formDataWithId?.amount;
    }

    // Validation: for OPD visit type allow zero amount and keep fields editable
    if (isOPDVisitType) {
      // Do not force amount=0; keep it editable. Do not remove payment/discount.
      if (!formDataWithId.unit || !formDataWithId.doctor || !formDataWithId.visitType || !formDataWithId.consultationDateTime) {
        toast.error('Please fill all the required fields (Unit, Doctor, Visit Type, Date & Time).');
        return;
      }
    } else {
      // For non-OPD visit types: require positive amounts and use Joi validation
      if (formData?.amount === "" || Number(formData?.amount || 0) <= 0) {
        toast.error("Amount must be greater than 0 for non-OPD visit types.");
        return;
      }
      if (formData?.payment?.amountPaid === "" || Number(formData?.payment?.amountPaid || 0) <= 0) {
        toast.error("Amount Paid must be greater than 0 for non-OPD visit types.");
        return;
      }
      
      // Use Joi validation for non-OPD visit types
      const { error } = ConvertConsultationSchema.validate({...formDataWithId, paymentStatus: selectedRow?.paymentStatus}, {abortEarly: false});
      
      if (error) {
        const errorFields = [];
        error.details.forEach((err) => errorFields.push(err?.context?.label));
        setErrors(errorFields);
        toast.error('Please fill all the required fields.');
        return;
      }
      
    }

    setIsLoading(true);
    const response = await createConsultationApi(formDataWithId);
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(response?.message || `Appointment added successfully`);
      navigate("/appointments");
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  useEffect(() => {
    setFormData((preValue) => {
      const newFormData = {
        ...preValue,
        UHID: selectedRow?.patientId?.UHID,
        unit: selectedRow?.unit,
        patientId: selectedRow?.patientId?._id,
        doctorName: selectedRow?.doctorName,
        visitType: selectedRow?.visitType,
        consultationTime: selectedRow?.appointmentDateTime
          ? dayjs(selectedRow?.appointmentDateTime).format("HH:mm")
          : null,
        consultationDate: selectedRow?.appointmentDateTime
          ? dayjs(selectedRow?.appointmentDateTime).format("YYYY-MM-DD")
          : null,
      };
      
      return newFormData;
    });
  }, [selectedRow]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        {selectedRowLoading ? (
          <Loader />
        ) : selectedRowError ? (
          <ErrorMessage />
        ) : (
          <Box
            component={"form"}
            onSubmit={handleSubmit}
            sx={{ width: "100%", pr: "80px", py: "20px" }}
          >
            {/* Form */}
            <Box
              sx={{
                padding: 2,
                borderRadius: 2,
              }}
            >
              <Button
                startIcon={<ArrowLeft />}
                onClick={() => navigate(-1)}
                sx={{
                  color: '#5D6A82',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  padding: 0,
                  transition: 'none !important',
                  marginBottom: '16px',
                }}
              >
                Back
              </Button>
              {/* Consultation Form */}
              <Typography
                sx={{
                  fontFamily: 'Open Sans',
                  fontWeight: 600,
                  fontSize: '20px',
                  lineHeight: '29px',
                  letterSpacing: '0px',
                  color: "#5D6A82",
                  marginBottom: "1rem",
                }}
              >
                Enter Consultation Details
              </Typography>

              {/* Patient Information */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4} md={3} xl={2}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "15rem",
                      border: "1px solid #5d6a82",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                      boxSizing: "border-box",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    {selectedRow?.patientId?.patientImage ? (
                      <Box
                        component="img"
                        src={selectedRow.patientId.patientImage}
                        alt="Patient"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          color: "#999",
                          fontSize: "14px",
                          textAlign: "center",
                        }}
                      >
                        No picture
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={8} md={9} xl={10}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" color="grey">
                        Patient Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="UHID"
                        name="UHID"
                        value={selectedRow?.patientId?.UHID || "-"}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Name"
                        name="name"
                        value={
                          selectedRow?.patientId?.firstName && selectedRow?.patientId?.lastName
                            ? `${selectedRow.patientId.firstName} ${selectedRow.patientId.lastName}`
                            : selectedRow?.patientId?.firstName || selectedRow?.patientId?.lastName || "-"
                        }
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Number"
                        name="phone"
                        value={selectedRow?.patientId?.phone || selectedRow?.patientId?.contactNumber || "-"}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Gender"
                        name="gender"
                        value={selectedRow?.patientId?.gender || "-"}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Address"
                        name="address"
                        value={selectedRow?.patientId?.address || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="City"
                        name="city"
                        value={selectedRow?.patientId?.city?.cityName || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="State"
                        name="state"
                        value={selectedRow?.patientId?.state?.stateName || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Pincode"
                        name="pinCode"
                        value={selectedRow?.patientId?.pinCode || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* Consultation Information */}
              <Box
                sx={{
                  padding: 2,
                  backgroundColor: "#f5faff",
                  borderRadius: 2,
                  my: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="subtitle1" color="grey">
                    Consultation Information
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    {unitList ? (
                      <Autocomplete
                        options={unitList || []}
                        getOptionLabel={(option) => option?.unitName || ""}
                        isOptionEqualToValue={(option, value) => {
                          // Handle cases where options might not be loaded yet
                          if (!option || !value) return false;
                          
                          // If value is a string (ID), compare with option ID
                          if (typeof value === 'string') return option._id === value;
                          
                          // If value is an object, compare IDs
                          if (value && typeof value === 'object' && value._id) {
                            return option._id === value._id;
                          }
                          
                          // Fallback: compare the entire objects
                          return JSON.stringify(option) === JSON.stringify(value);
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Unit" 
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: errors?.includes('unit') ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }} 
                          />
                        )}
                        value={formData?.unit || null}
                        onChange={(_, newValue) =>
                          handleDoctorUnitChange({
                            target: { name: "unit", value: newValue },
                          })
                        }
                      />
                    ) : (
                      <TextField label="Unit" value="Loading..." disabled fullWidth />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    {doctorsList ? (
                      <Autocomplete
                        options={  formData?.unit
                          ? doctorsList.filter(
                              (d) =>
                                d?.role?.role === "Doctor" &&
                                d?.unit?._id === formData?.unit?._id
                            )
                          : doctorsList.filter(
                              (d) => d?.role?.role === "Doctor"
                            ) || []
                        }
                        getOptionLabel={(option) =>
                          `${option?.firstName || ""} ${
                            option?.lastName || ""
                          } (${option?.userId || ""})`
                        }
                        isOptionEqualToValue={(option, value) => {
                          // Handle cases where options might not be loaded yet
                          if (!option || !value) return false;
                          
                          // If value is a string (ID), compare with option ID
                          if (typeof value === 'string') return option._id === value;
                          
                          // If value is an object, compare IDs
                          if (value && typeof value === 'object' && value._id) {
                            return option._id === value._id;
                          }
                          
                          // Fallback: compare the entire objects
                          return JSON.stringify(option) === JSON.stringify(value);
                        }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            label="Doctor Name"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  border: errors?.includes('doctorName') ? '1px solid red' : '1px solid inherit',
                                },
                              },
                            }} 
                           />
                        )}
                        value={formData?.doctorName || null}
                        onChange={(_, newValue) =>
                          handleDoctorUnitChange({
                            target: { name: "doctorName", value: newValue },
                          })
                        }
                      />
                    ) : (
                      <TextField label="Doctor Name" value="Loading..." disabled fullWidth />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    {visitTypeList ? (
                      <Autocomplete
                        options={visitTypeList || []}
                        getOptionLabel={(option) =>
                          `${option?.name || ""} (${option?.visitType || ""})`
                        }
                        isOptionEqualToValue={(option, value) => {
                          // Handle cases where options might not be loaded yet
                          if (!option || !value) return false;
                          
                          // If value is a string (ID), compare with option ID
                          if (typeof value === 'string') return option._id === value;
                          
                          // If value is an object, compare IDs
                          if (value && typeof value === 'object' && value._id) {
                            return option._id === value._id;
                          }
                          
                          // Fallback: compare the entire objects
                          return JSON.stringify(option) === JSON.stringify(value);
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Visit Type" 
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: errors?.includes('visitType') ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }} 
                          />
                        )}
                        value={formData?.visitType || null}
                        onChange={(_, newValue) =>
                          handleInputChange({
                            target: { name: "visitType", value: newValue },
                          })
                        }
                      />
                    ) : (
                      <TextField label="Visit Type" value="Loading..." disabled fullWidth />
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <DatePicker
                      label="Date"
                      maxDate={dayjs()}
                      slotProps={{
                        textField: { fullWidth: true, required: true },
                      }}
                      value={
                        formData?.consultationDate
                          ? dayjs(formData.consultationDate)
                          : null
                      }
                      onChange={(newValue) =>
                        handleInputChange({
                          target: { name: "consultationDate", value: newValue },
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TimePicker
                      label="Time"
                      slotProps={{
                        textField: { fullWidth: true, required: true },
                      }}
                      value={
                        formData?.consultationTime
                          ? dayjs(formData?.consultationTime, "HH:mm")
                          : null
                      }
                      onChange={(newValue) => {
                        handleInputChange({
                          target: {
                            name: "consultationTime",
                            value: newValue,
                          },
                        });
                      }}
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                      }}
                    />
                  </Grid>
                  {!selectedRow?.invoice && (
                    <Grid item xs={12} sm={6} md={4}>
                      {isOPDVisitType ? (
                        <Box
                          sx={{
                            padding: 2,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 1,
                            border: "1px dashed #ccc",
                            textAlign: "center"
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Amount not required for OPD visit type
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <TextField
                            type="number"
                            label="Amount"
                            name="amount"
                            value={formData?.amount || ""}
                            onChange={handleInputChange}
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  border: errors?.includes('amount') ? '1px solid red' : '1px solid inherit',
                                },
                              },
                            }} 
                          />
                        </>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Billing Information */}
              {!selectedRow?.invoice ? (
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                  }}
                >
                  {/* Overall Discount */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: 2,
                    }}
                  >
                    {!isOPDVisitType && (
                      <Button
                        variant="outlined"
                        sx={{ textTransform: "none" }}
                        onClick={handleModalOpen}
                        disabled={!isTransactionTypeSet}
                      >
                        Overall Discount
                      </Button>
                    )}

                    <DiscountModal
                      open={modalOpen}
                      onClose={handleModalClose}
                      handleChange={handleInputChange}
                      formData={formData}
                    />
                  </Box>

                  {/* Amount To Be Paid */}
                  <Box
                    sx={{
                      padding: 2,
                      backgroundColor: "#EAEAEA",
                      borderRadius: 2,
                      mb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {isOPDVisitType ? (
                      <Typography variant="body1" sx={{ fontWeight: "bold", color: "text.secondary" }}>
                        Amount section not applicable for OPD visit type
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          Amount to be paid: ₹
                          {formData?.amount -
                            (formData?.discount?.discountType === "Percentage"
                              ? formData?.discount?.discountPercentage *
                                formData?.amount *
                                0.01
                              : formData?.discount?.discountAmount)}
                        </Typography>
                        <Typography sx={{ fontWeight: "bold" }}>|</Typography>
                        <Typography sx={{ fontWeight: "bold" }}>
                          Net Expense: ₹{formData?.amount}
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Transaction Details */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      {transactionTypeList ? (
                        <Autocomplete
                          options={transactionTypeList || []}
                          getOptionLabel={(option) =>
                            `${option?.name || ""} (${option?.paymentMethod})`
                          }
                          isOptionEqualToValue={(option, value) => {
                            // Handle cases where options might not be loaded yet
                            if (!option || !value) return false;
                            
                            // If value is a string (ID), compare with option ID
                            if (typeof value === 'string') return option._id === value;
                            
                            // If value is an object, compare IDs
                            if (value && typeof value === 'object' && value._id) {
                              return option._id === value._id;
                            }
                            
                            // Fallback: compare the entire objects
                            return JSON.stringify(option) === JSON.stringify(value);
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Transaction Type" 
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                  border: (errors?.includes('payment.transactionType') ||errors?.includes('payment'))  ? '1px solid red' : '1px solid inherit',
                                },
                              },
                            }} 
                            />
                          )}
                          value={formData?.payment?.transactionType || null}
                          onChange={(_, newValue) => {
                            setIsTransactionTypeSet(!!newValue);
                            handleInputChange({
                              target: {
                                name: "transactionType",
                                value: newValue,
                              },
                            });
                          }}
                        />
                      ) : (
                        <TextField label="Transaction Type" value="Loading..." disabled fullWidth />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl
                        sx={{ minWidth: "100px" }}
                        required={isTransactionTypeSet}
                        fullWidth
                      >
                        <InputLabel id="payment-type-id">
                          Payment Type
                        </InputLabel>
                        <Select
                          labelId="payment-type-id"
                          label="Payment Type"
                          name="paymentType"
                          value={formData?.payment?.paymentType || ""}
                          onChange={handleInputChange}
                          sx={{
                            border: (errors?.includes('payment.paymentType') ||errors?.includes('payment'))  ? '1px solid red' : '1px solid inherit',
                          }} 
                        >
                          <MenuItem value="InvoicePayment">
                            Invoice Payment
                          </MenuItem>
                          <MenuItem value="SelfPayment">Self Payment</MenuItem>
                          <MenuItem value="Refund">Refund</MenuItem>
                          <MenuItem value="IPDAdvance">IPD Advance</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      {formData?.payment?.transactionType?.name ===
                      "QR Payment" ? (
                        <TextField
                          label="Transaction ID"
                          name="transactionId"
                          required={isTransactionTypeSet}
                          value={formData?.payment?.transactionId || ""}
                          onChange={handleInputChange}
                          fullWidth
                          sx={{ bgcolor: "white" }}
                        />
                      ) : (
                        <TextField
                          label="Transfer Bank A/C"
                          name="transferBankAccount"
                          required={isTransactionTypeSet}
                          value={
                            formData?.payment?.transactionType?.bankDetails?.find(
                              (bankDetail) => bankDetail?.default
                            )?.accountNumber || ""
                          }
                          slotProps={{ input: { readOnly: true } }}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: (errors?.includes('payment.transactionType') ||errors?.includes('payment'))  ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }}
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      {isOPDVisitType ? (
                        <Box
                          sx={{
                            padding: 2,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 1,
                            border: "1px dashed #ccc",
                            textAlign: "center"
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Amount not required for OPD visit type
                          </Typography>
                        </Box>
                      ) : (
                        <TextField
                          type="number"
                          label="Amount (₹)"
                          name="amountPaid"
                          // required={isTransactionTypeSet}
                          value={formData?.payment?.amountPaid || ""}
                          onChange={handleInputChange}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: (errors?.includes('payment.amountPaid') ||errors?.includes('payment'))  ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }} 
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      {isOPDVisitType ? (
                        <Box
                          sx={{
                            padding: 2,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 1,
                            border: "1px dashed #ccc",
                            textAlign: "center"
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Amount not required for OPD visit type
                          </Typography>
                        </Box>
                      ) : (
                        <TextField
                          type="number"
                          label="Confirm Amount (₹)"
                          name="confirmAmountPaid"
                          // required={isTransactionTypeSet}
                          value={confirmAmountPaid || ""}
                          onChange={(e) => setConfirmAmountPaid(e.target.value)}
                          fullWidth
                          error={!amountsMatch && confirmAmountPaid !== ""}
                          helperText={
                            !amountsMatch && confirmAmountPaid !== ""
                              ? isPendingPayment 
                                ? `Amount must be between ₹0 and ₹${formData?.payment?.amountPaid || 0}`
                                : "Amount does not match!"
                              : ""
                          }
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: ((errors?.includes('payment.amountPaid') ||errors?.includes('payment')) && !confirmAmountPaid)  ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }} 
                        />
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Remarks"
                        // required={isTransactionTypeSet}
                        name="remarks"
                        value={formData?.payment?.remarks || ""}
                        onChange={handleInputChange}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: ((errors?.includes('payment.remarks') ||errors?.includes('payment')))  ? '1px solid red' : '1px solid inherit',
                            },
                          },
                        }} 
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", pb: "15px" }}
                >
                  Amount has been already paid
                </Typography>
              )}

              {/* Submit Button */}
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ padding: "10px 40px", textTransform: "none" }}
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={24} /> : `Submit`}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
}
